import { useState, useEffect } from "react";
import { useSelector } from "react-redux";

// Add the id if missing, remove it if present.
const toggleId = (ids, id) =>
    ids.includes(id) ? ids.filter(existing => existing !== id) : [...ids, id];

const useUnivWorkspaceFilterHook = () => {
    // States
    const [openId, setOpenId] = useState(null);
    const [
        unselected_organization_ids,
        setUnselectedOrgIds
    ] = useState([])
    const [
        unselected_workspace_ids,
        setUnselectedWorkspaceIds
    ] = useState([])

    // Redux (filter data is loaded by useNotificationFilter)
    const {
        organizations,
    } = useSelector(state => state.notification_filter);

    // Vars
    const allSelected = unselected_organization_ids?.length === 0 && unselected_workspace_ids?.length === 0;
    const workspaces = organizations?.flatMap(org => org.workspaces) || [];
    const selectedWorkspaces = workspaces?.filter(ws => !unselected_workspace_ids?.includes(ws.id) && !unselected_organization_ids?.includes(ws.organization_id));

    // Functions
    const handleToggleOrg = (organization) => {
        const workspaceIdsInOrg = organization.workspaces.map(w => w.id);

        setUnselectedOrgIds(prev => toggleId(prev, organization.id));
        setUnselectedWorkspaceIds(prev => prev.filter(id => !workspaceIdsInOrg.includes(id)));
    }

    const handleWorkspaceToggle = (workspace) => {
        const organization = organizations?.find(org => org.id === workspace.organization_id);

        // No matching org: simply toggle the workspace on its own
        if (!organization) {
            setUnselectedWorkspaceIds(prev => toggleId(prev, workspace.id));
            return;
        }

        const workspaceIdsInOrg = organization.workspaces.map(w => w.id);

        // Org is fully unselected: selecting one workspace re-selects the org
        // while unselecting every other workspace within it
        if (unselected_organization_ids.includes(organization.id)) {
            setUnselectedOrgIds(prev => prev.filter(id => id !== organization.id));
            setUnselectedWorkspaceIds(prev => [
                ...prev,
                ...workspaceIdsInOrg.filter(id => id !== workspace.id),
            ]);
            return;
        }

        const updatedWorkspaceIds = toggleId(unselected_workspace_ids, workspace.id);
        const allWorkspacesUnselected = workspaceIdsInOrg.every(id => updatedWorkspaceIds.includes(id));

        // Last workspace just unselected: collapse the whole org into an org-level unselect
        if (allWorkspacesUnselected) {
            setUnselectedOrgIds(prev => [...prev, organization.id]);
            setUnselectedWorkspaceIds(updatedWorkspaceIds.filter(id => !workspaceIdsInOrg.includes(id)));
            return;
        }

        // Normal case: apply the workspace-level toggle, keep the org selected
        setUnselectedWorkspaceIds(updatedWorkspaceIds);
        setUnselectedOrgIds(prev => prev.filter(id => id !== organization.id));
    }

    const handleClear = () => {
        setUnselectedOrgIds([]);
        setUnselectedWorkspaceIds([]);
    }

    const isSelected = (item, type = '') => {
        if(type === 'organization') {
            return !unselected_organization_ids.includes(item.id);
        } else if(type === 'workspace') {
            if(unselected_organization_ids.includes(item.organization_id)) { return false; }
            return !unselected_workspace_ids.includes(item.id);
        }

        return true;
    }

    return {
        organizations,
        unselected_organization_ids,
        unselected_workspace_ids,
        selectedWorkspaces,
        allSelected,
        openId,
        setOpenId,
        isSelected,
        handleWorkspaceToggle,
        handleToggleOrg,
        handleClear,
    };
};

export default useUnivWorkspaceFilterHook;
